"use client";

import { useMemo } from "react";
import { useTripData } from "@/lib/trip-data-context";
import { formatDay } from "@/lib/format";
import { Card, InnerCard, Badge, TabLoading, EmptyState } from "@/components/trip/ui";

export default function HotelsPage() {
  const { data, loading } = useTripData();

  // Derive hotel stays: which dates map to which accommodation. Trips with a
  // day-by-day itinerary get date ranges from day assignments; trips without
  // one fall back to the accommodation's own stayDates.
  const stays = useMemo(() => {
    if (!data) return [];
    const accomMap = new Map(data.accommodations.map((a) => [a.id, a]));
    const result: { accom: typeof data.accommodations[0]; dates: string[] }[] = [];

    for (const day of data.days) {
      if (!day.accommodationId) continue;
      const accom = accomMap.get(day.accommodationId);
      if (!accom) continue;
      const last = result[result.length - 1];
      if (last && last.accom.id === accom.id) {
        last.dates.push(day.date);
      } else {
        result.push({ accom, dates: [day.date] });
      }
    }

    // Append accommodations not pinned to any day (no itinerary yet).
    const covered = new Set(result.map((s) => s.accom.id));
    for (const accom of data.accommodations) {
      if (!covered.has(accom.id)) {
        result.push({ accom, dates: [] });
      }
    }
    return result;
  }, [data]);

  if (loading || !data) {
    return <TabLoading />;
  }

  if (stays.length === 0) {
    return <EmptyState>No hotels booked yet.</EmptyState>;
  }

  return (
    <div>
      {stays.map((stay) => {
        const range = stay.dates.length > 0
          ? { start: stay.dates[0], end: stay.dates[stay.dates.length - 1] }
          : stay.accom.stayDates ?? null;
        const nights = stay.dates.length || stay.accom.nights;
        const dateLabel = range
          ? (range.start === range.end ? formatDay(range.start) : `${formatDay(range.start)} → ${formatDay(range.end)}`)
          : null;
        return (
        <Card key={stay.accom.id + (stay.dates[0] ?? "standalone")}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{stay.accom.name}</div>
            <Badge>BOOKED</Badge>
          </div>
          <InnerCard>
            <div style={{ fontSize: 12, color: "var(--text3)", fontFamily: "var(--mono)", marginBottom: 6 }}>
              {dateLabel && <>{dateLabel}{" · "}</>}{nights} {nights === 1 ? "night" : "nights"}
            </div>
            <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 4 }}>
              {stay.accom.address}
            </div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 4 }}>
              {stay.accom.cost}
            </div>
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 6 }}>
              Conf: {stay.accom.confirmationNumber}
            </div>
            <div style={{ fontSize: 11, color: "var(--text3)" }}>
              Check-in: {stay.accom.checkIn}
            </div>
            {stay.accom.hostContact && (
              <div style={{ fontSize: 11, color: "var(--text3)" }}>
                Host: {stay.accom.hostContact}
              </div>
            )}
            {stay.accom.navigationUri && (
              <a
                href={stay.accom.navigationUri}
                target="_blank"
                rel="noopener"
                style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, fontSize: 12, color: "var(--accent)", textDecoration: "none" }}
              >
                Open in Maps
              </a>
            )}
          </InnerCard>
        </Card>
        );
      })}
    </div>
  );
}
